import React from "react";
import { useState, useEffect, useRef, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  SessionExpired,
  SessionAboutToExpire,
} from "components/modals/SessionTimeout";
import { logout, refreshToken, isAuthenticated } from "store/actions";

const LOCKTIME = 40 * 60 * 1000; // 40 minutes
const AUTHTIME = 50 * 60 * 1000; // 50 minutes
const REFRESH_TOKEN_TIME = 30 * 60 * 1000; // 30 minutes

const ScreenLocker = ({ children }) => {
  const [locked, setLocked] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userData = useSelector((state) => state.auth.authData);
  const is_logged_in = useSelector((state) => state.auth.logged_in);
  const userRef = useRef(userData);
  const lockTimeoutRef = useRef(null);
  const authTimeoutRef = useRef(null);

  const resetTimers = () => {
    clearTimeout(lockTimeoutRef.current);
    clearTimeout(authTimeoutRef.current);

    // Set the lock timer (for session expire modal)
    lockTimeoutRef.current = setTimeout(() => {
      if (userRef.current) {
        // Set the auth timer (for logging out)
        authTimeoutRef.current = setTimeout(() => {
          dispatch(logout());
        }, AUTHTIME - LOCKTIME); // Logout 10 minutes after session modal shows
        setLocked(true);
      }
    }, LOCKTIME);
  };

  const handleUserActivity = () => {
    if (!locked && is_logged_in) {
      resetTimers();
    }
  };

  const redirectLogin = () => {
    setLocked(false);
    navigate("/login", { replace: true });
  };

  const getRefreshToken = () => {
    const userData = isAuthenticated();
    if (userData) {
      const { refresh_token } = userData;
      dispatch(
        refreshToken({
          email_id: localStorage.getItem("email_id"),
          refresh_token: refresh_token,
        })
      );
    } else {
      dispatch(logout());
      setLocked(true);
    }
  };

  useEffect(() => {
    // Initial setup: start token refresh and set up lock timers
    if (userData?.refresh_token) getRefreshToken();

    const refreshInterval = setInterval(() => {
      if (userRef.current) getRefreshToken();
    }, REFRESH_TOKEN_TIME);

    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (is_logged_in) {
      resetTimers();
    } else {
      clearTimeout(lockTimeoutRef.current);
      clearTimeout(authTimeoutRef.current);
    }
  }, [is_logged_in]);

  useEffect(() => {
    userRef.current = userData;
  }, [userData]);

  return (
    <Fragment>
      <div
        onMouseMove={handleUserActivity}
        onKeyDown={handleUserActivity}
        onKeyUp={handleUserActivity}
      >
        {children}
      </div>

      {locked &&
        (userData ? (
          <SessionAboutToExpire
            close={() => {
              setLocked(false);
              resetTimers();
            }}
            logout={redirectLogin}
          />
        ) : (
          <SessionExpired logout={redirectLogin} />
        ))}
    </Fragment>
  );
};

export default ScreenLocker;
